import ChartType from '@opsbase/lib-chart/ChartType';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import useSelf from '@opsbase/react-ctrl-self/useSelf/useSelf';
import useDimensions from '@opsbase/react-hooks/useDimensions';
import useCss from '@opsbase/web-css';
import ConfirmDialog from '@opsbase/web-dialogs/ConfirmDialog';
import Card from '@opsbase/web-layout/Card';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Vertical from '@opsbase/web-layout/Vertical';
import Text from '@opsbase/web-ui/Text';
import omit from 'lodash/omit';
import React, { useCallback, useMemo, useState } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import DashboardWidgetViewer from '../DashboardWidgetViewer';
const makeMyClasses = () => ({
    root: {
        '& .react-grid-item.react-grid-placeholder': {
            backgroundColor: Palette.secondary.main + ' !important',
            opacity: '0.4 !important',
        },
    },
});
export function Dashboard(props) {
    const { isEditable = false, onChange, onOpen, dashboardWidgets } = props;
    const [paneRef, paneDims] = useDimensions();
    const [deleting, setDeleting] = useState();
    const myCss = useCss(makeMyClasses);
    const self = useSelf();
    const handleLayoutChange = useCallback(v => onChange(v
        .map(vv => { var _a; return ({ ...vv, i: (_a = vv === null || vv === void 0 ? void 0 : vv.i) === null || _a === void 0 ? void 0 : _a.split('_#')[0] }); })
        .map(vv => ({
        ...dashboardWidgets.find(p => p.key === vv.i),
        coords: omit(vv, ['i']),
    }))), [onChange, dashboardWidgets]);
    const handleRename = useCallback((v, title) => {
        onChange(dashboardWidgets.map(p => p !== v
            ? p
            : {
                ...p,
                title,
            }));
    }, [dashboardWidgets, onChange]);
    const handleRemove = useCallback(v => setDeleting(v), []);
    const handleCancelRemove = useCallback(() => setDeleting(undefined), []);
    const handleConfirmRemove = useCallback(async () => {
        onChange(dashboardWidgets.filter(p => p !== deleting));
        setDeleting(undefined);
        return true;
    }, [onChange, dashboardWidgets, deleting]);
    const gridLayout = useMemo(() => {
        if (!(paneDims === null || paneDims === void 0 ? void 0 : paneDims.width))
            return null;
        return (React.createElement("div", { className: myCss.root },
            !!dashboardWidgets.length && (React.createElement(GridLayout, { isDraggable: isEditable, isResizable: isEditable, layout: dashboardWidgets.map(({ key, chart, coords }) => ({
                    i: key + '_#' + (self === null || self === void 0 ? void 0 : self.organisationId),
                    ...normCoordsPerChartType(coords, chart === null || chart === void 0 ? void 0 : chart.chartType),
                })), draggableCancel: '.dragcancel', cols: 12, rowHeight: 60, resizeHandles: isEditable ? ['se'] : [], width: paneDims.width, onLayoutChange: handleLayoutChange }, dashboardWidgets.map(dashboardWidget => (React.createElement(Card, { key: dashboardWidget.key + '_#' + (self === null || self === void 0 ? void 0 : self.organisationId), padding: true, grow: true },
                React.createElement(DashboardWidgetViewer, { isEditable: isEditable, dashboardWidget: dashboardWidget, onOpen: onOpen, onDelete: handleRemove, onRename: handleRename })))))),
            !dashboardWidgets.length && (React.createElement(Horizontal, { stretch: true, padding: true },
                React.createElement(Card, { grow: true, padding: true },
                    React.createElement(Vertical, { padding: true, gutter: true },
                        React.createElement(Text, { variant: "h1" }, "Dashboard currently empty"),
                        React.createElement(Text, null, "You can add charts to your dashboard from either the Ops Log, the Issues Log or from the plus button below."))))),
            React.createElement(ConfirmDialog, { open: !!deleting, color: Palette.error, onConfirm: handleConfirmRemove, onClose: handleCancelRemove },
                React.createElement(Text, null, i18n.t('confirm-remove-from-dashboard')))));
    }, [
        paneDims,
        myCss,
        dashboardWidgets,
        isEditable,
        handleLayoutChange,
        deleting,
        handleConfirmRemove,
        handleCancelRemove,
        self,
        onOpen,
        handleRemove,
        handleRename,
    ]);
    return (React.createElement("div", { style: { position: 'relative', overflowY: 'scroll', overflowX: 'hidden' }, ref: paneRef }, gridLayout));
}
export default Dashboard;
function normCoordsPerChartType(coords, type) {
    switch (type) {
        case ChartType.PIE2D:
        case ChartType.BAR2D:
            return {
                x: (coords === null || coords === void 0 ? void 0 : coords.x) || 0,
                y: (coords === null || coords === void 0 ? void 0 : coords.y) || 0,
                w: Math.max(coords === null || coords === void 0 ? void 0 : coords.w, 2),
                h: Math.max(coords === null || coords === void 0 ? void 0 : coords.h, 2),
                minW: 2,
                maxW: 6,
                minH: 2,
                maxH: 6,
            };
        default:
            return {
                x: (coords === null || coords === void 0 ? void 0 : coords.x) || 0,
                y: (coords === null || coords === void 0 ? void 0 : coords.y) || 0,
                w: Math.max(coords === null || coords === void 0 ? void 0 : coords.w, 2),
                h: Math.max(coords === null || coords === void 0 ? void 0 : coords.h, 2),
                minW: 3,
                maxW: 12,
                minH: 2,
                maxH: 9,
            };
    }
}
